import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { MiniHeader } from "../components/MiniHeader";
import { PinkButton } from "../components/Buttons";
import { Chuckle, Drip, Shiver, Bob } from "../components/Characters";
import { api, errorText } from "../lib/api";
import { useAuth } from "../lib/auth";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const inputCls = "mt-2 w-full rounded-2xl border-2 border-ink bg-paper px-4 py-3 font-body text-lg text-ink placeholder:text-ink/50 focus:border-pop";

const Field = ({ id, label, ...props }) => (
  <div>
    <label htmlFor={id} className="font-body text-base font-semibold">{label}</label>
    <input id={id} data-testid={id} className={inputCls} required {...props} />
  </div>
);

const Tabs = ({ mode, setMode }) => (
  <div role="tablist" className="grid grid-cols-2 rounded-full bg-ink/5 p-1">
    {[["register", "Create account"], ["login", "Log in"]].map(([m, label]) => (
      <button key={m} type="button" role="tab" aria-selected={mode === m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
        className={`rounded-full px-4 py-2.5 font-head text-base transition-colors ${mode === m ? "bg-ink text-paper" : "text-ink hover:bg-ink/10"}`}>
        {label}
      </button>
    ))}
  </div>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/account" replace />;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      setUser((await api.post(`/auth/${mode}`, body)).data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <MiniHeader />
      <main className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-12 sm:px-8 lg:grid-cols-2 lg:py-20">
        <div>
          <p className="font-body text-base font-semibold text-ink/80">Filmmaker login</p>
          <h1 className="mt-3 font-head text-5xl leading-none tracking-tight sm:text-6xl">Enter your films.</h1>
          <p className="mt-5 max-w-md font-body text-lg leading-relaxed text-ink/85">Create an account to submit up to 2 films, each in a different category: Laughter, Tears or Fears.</p>
          <div className="mt-8 hidden gap-3 lg:flex">
            <Bob><Chuckle className="h-20 w-20" /></Bob><Bob delay={0.6}><Drip className="h-20 w-20" /></Bob><Bob delay={1.2}><Shiver className="h-20 w-20" /></Bob>
          </div>
        </div>
        <div className="rounded-[36px] border-2 border-ink bg-white p-6 shadow-[8px_8px_0_#1B1340] sm:p-9">
          <Tabs mode={mode} setMode={(m) => { setMode(m); setError(""); }} />
          <button type="button" data-testid="google-login-button" onClick={googleLogin}
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border-2 border-ink px-5 py-3 font-head text-lg hover:bg-ink hover:text-paper">
            Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 font-body text-sm text-ink/70"><span className="h-px flex-1 bg-ink/20" />or use email<span className="h-px flex-1 bg-ink/20" /></div>
          <form onSubmit={submit} className="flex flex-col gap-4" data-testid="auth-form">
            {mode === "register" && <Field id="auth-name-input" label="Your name" value={form.name} onChange={set("name")} autoComplete="name" maxLength={80} />}
            <Field id="auth-email-input" label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" />
            <Field id="auth-password-input" label="Password" type="password" value={form.password} onChange={set("password")} minLength={mode === "register" ? 8 : undefined}
              autoComplete={mode === "register" ? "new-password" : "current-password"} placeholder={mode === "register" ? "At least 8 characters" : ""} />
            {error && <p data-testid="auth-error-message" role="alert" className="rounded-xl bg-pop/15 px-4 py-2 font-body font-semibold">{error}</p>}
            <PinkButton type="submit" data-testid="auth-submit-button" disabled={busy} className="mt-2 w-full">
              {busy && <Loader2 size={18} className="animate-spin" />} {mode === "register" ? "Create account" : "Log in"}
            </PinkButton>
          </form>
        </div>
      </main>
    </div>
  );
}
